import React, {
    FC,
    lazy,
    Suspense,
} from 'react';
import {
    BrowserRouter,
    Route,
    Routes,
} from 'react-router-dom';
import {
    RecoilRoot,
} from 'recoil';

import {
    Container,
    Loading,
} from './components/other';
import {
    PATH,
} from './pages/constants';
import {
    useRedirectProtocol,
} from './tools/hooks';

const SignIn = lazy(() => import('./pages/SignIn'));

const Payments = lazy(() => import('./pages/Payments'));
const Payment = lazy(() => import('./pages/Payment'));
const Complaints = lazy(() => import('./pages/Complaints'));
const Complaint = lazy(() => import('./pages/Complaint'));
const Accounts = lazy(() => import('./pages/Accounts'));
const Account = lazy(() => import('./pages/Account'));
const AccountDrafts = lazy(() => import('./pages/AccountDrafts'));
const AccountPools = lazy(() => import('./pages/AccountPools'));
const AccountPool = lazy(() => import('./pages/AccountPool'));
const Agents = lazy(() => import('./pages/Agents'));
const Agent = lazy(() => import('./pages/Agent'));
const PaymentMethods = lazy(() => import('./pages/PaymentMethods'));
const PaymentMethod = lazy(() => import('./pages/PaymentMethod'));
const Merchant = lazy(() => import('./pages/Merchant'));
const MerchantPaymentMethodAccountPoolInfos = lazy(() => import('./pages/MerchantPaymentMethodAccountPoolInfos'));
const Merchants = lazy(() => import('./pages/Merchants'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Proxies = lazy(() => import('./pages/Proxies'));
const Proxy = lazy(() => import('./pages/Proxy'));
const StatisticsAgent = lazy(() => import('./pages/StatisticsAgent'));
const Users = lazy(() => import('./pages/Users'));
const User = lazy(() => import('./pages/User'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminAccountConversionSetting = lazy(() => import('./pages/AdminAccountConversionSetting'));
const AdminPrepaySetting = lazy(() => import('./pages/AdminPrepaySetting'));
const AdminAntiFraudSetting = lazy(() => import('./pages/AdminAntiFraudSetting'));
const AdminBlackListSetting = lazy(() => import('./pages/AdminMerchantUserIdBlackListSetting'));

const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));

const App: FC = () => {
    useRedirectProtocol();

    return (
        <RecoilRoot>
            <BrowserRouter>
                <Container>
                    <Suspense fallback={<Loading isPage={true}/>}>
                        <Routes>
                            <Route
                                path={PATH.SIGN_IN}
                                element={<SignIn/>}
                            />
                            <Route
                                path={`${PATH.PAYMENTS}/:tab?`}
                                element={<Payments/>}
                            />
                            <Route
                                path={`${PATH.PAYMENT}/:actionPath/:id?`}
                                element={<Payment/>}
                            />
                            <Route
                                path={`${PATH.COMPLAINTS}/:tab?`}
                                element={<Complaints/>}
                            />
                            <Route
                                path={`${PATH.COMPLAINT}/:actionPath/:id?`}
                                element={<Complaint/>}
                            />
                            <Route
                                path={`${PATH.ACCOUNTS}/:tab?`}
                                element={<Accounts/>}
                            />
                            <Route
                                path={`${PATH.ACCOUNT}/:actionPath/:id?`}
                                element={<Account/>}
                            />
                            <Route
                                path={PATH.ACCOUNT_DRAFTS}
                                element={<AccountDrafts/>}
                            />
                            <Route
                                path={PATH.ACCOUNT_POOLS}
                                element={<AccountPools/>}
                            />
                            <Route
                                path={`${PATH.ACCOUNT_POOL}/:actionPath/:id?`}
                                element={<AccountPool/>}
                            />
                            <Route
                                path={PATH.AGENTS}
                                element={<Agents/>}
                            />
                            <Route
                                path={`${PATH.AGENT}/:actionPath/:tab?/:id?`}
                                element={<Agent/>}
                            />
                            <Route
                                path={`${PATH.PAYMENT_METHODS}/:tab?`}
                                element={<PaymentMethods/>}
                            />
                            <Route
                                path={`${PATH.PAYMENT_METHOD}/:actionPath/:id?`}
                                element={<PaymentMethod/>}
                            />
                            <Route
                                path={PATH.MERCHANTS}
                                element={<Merchants/>}
                            />
                            <Route
                                path={`${PATH.MERCHANT}/:actionPath/:tab?/:id?`}
                                element={<Merchant/>}
                            />
                            <Route
                                path={`${PATH.MERCHANT_POOLS}/:merchantId`}
                                element={<MerchantPaymentMethodAccountPoolInfos/>}
                            />
                            <Route
                                path={PATH.TRANSACTIONS}
                                element={<Transactions/>}
                            />
                            <Route
                                path={PATH.PROXIES}
                                element={<Proxies/>}
                            />
                            <Route
                                path={`${PATH.PROXY}/:actionPath/:id?`}
                                element={<Proxy/>}
                            />
                            <Route
                                path={PATH.STATISTICS_AGENT}
                                element={<StatisticsAgent/>}
                            />
                            <Route
                                path={PATH.USERS}
                                element={<Users/>}
                            />
                            <Route
                                path={`${PATH.USER}/:actionPath/:id?`}
                                element={<User/>}
                            />
                            <Route
                                path={PATH.ADMIN_PANEL}
                                element={<AdminPanel/>}
                            />
                            <Route
                                path={PATH.ADMIN_ACCOUNT_CONVERSION_SETTING}
                                element={<AdminAccountConversionSetting/>}
                            />
                            <Route
                                path={PATH.ADMIN_PREPAY_SETTING}
                                element={<AdminPrepaySetting/>}
                            />
                            <Route
                                path={PATH.ADMIN_ANTI_FRAUD_SETTING}
                                element={<AdminAntiFraudSetting/>}
                            />
                            <Route
                                path={PATH.ADMIN_BLACK_LIST_SETTING}
                                element={<AdminBlackListSetting/>}
                            />
                            <Route
                                path={PATH.SETTINGS}
                                element={<Settings/>}
                            />
                            <Route
                                path={'*'}
                                element={<NotFound/>}
                            />
                        </Routes>
                    </Suspense>
                </Container>
            </BrowserRouter>
        </RecoilRoot>
    );
};

export default App;
